import {Button} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon, UIIconProps} from '@dropbox/dig-icons';
import {AnalyticsLine, CloudDownloadLine, RocketLine, TargetLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {aiDrawerViewAtom, openAiSearchAndAskAtom} from 'atoms/aiSearch';
import {AI_SEARCH_SUGGESTIONS} from 'components/ai/aiQueryConversation';
import {AvatarIcon} from 'components/DSYS/Avatar';
import {CompanyAskGlyph} from 'components/DSYS/CompanyOSLogo';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {ComponentProps} from 'react';

// The differentiator vs. external MCP clients: the connectors this page onboards
// get read-only capabilities only (no transform tools), so Ask is the only client
// that can reshape, combine, and export in-app.
const HIGHLIGHT_SPECS: {
  id: string;
  titleKey: string;
  descriptionKey: string;
  icon: UIIconProps['src'];
}[] = [
  {
    id: 'faster_answers',
    titleKey: 'mcp_setup_builtin_faster_answers_title',
    descriptionKey: 'mcp_setup_builtin_faster_answers_description',
    icon: RocketLine,
  },
  {
    id: 'accurate',
    titleKey: 'mcp_setup_builtin_accurate_title',
    descriptionKey: 'mcp_setup_builtin_accurate_description',
    icon: TargetLine,
  },
  {
    id: 'pivot',
    titleKey: 'mcp_setup_builtin_pivot_title',
    descriptionKey: 'mcp_setup_builtin_pivot_description',
    icon: AnalyticsLine,
  },
  {
    id: 'export',
    titleKey: 'mcp_setup_builtin_export_title',
    descriptionKey: 'mcp_setup_builtin_export_description',
    icon: CloudDownloadLine,
  },
];

/**
 * The "built-in" client panel on the MCP setup page. Instead of install steps, it
 * pitches Ask (the in-app agent) as the zero-setup client that can do the
 * analytical work — pivot, combine, export — that read-only MCP connectors cannot.
 */
// Text is inline by default; wrap in a Box so stacked lines don't run together
// (mirrors the Body helper in McpSetup).
const Body = ({children, ...props}: ComponentProps<typeof Text>) => (
  <Box>
    <Text {...props}>{children}</Text>
  </Box>
);

export const McpBuiltInGuide = () => {
  const openAsk = useSetAtom(openAiSearchAndAskAtom);
  const setDrawerView = useSetAtom(aiDrawerViewAtom);

  const runPrompt = (prompt: string) => {
    analyticsLogger().logEvent('MCP_SETUP_CTA_CLICKED', {target: 'builtin_ask_prompt'});
    openAsk(prompt, 'mcp_setup');
  };

  const openEmpty = () => {
    analyticsLogger().logEvent('MCP_SETUP_CTA_CLICKED', {target: 'builtin_ask'});
    setDrawerView('expanded');
  };

  return (
    <Stack gap="20">
      <Stack gap="8">
        <Box>
          <Button
            variant="outline"
            withIconStart={<UIIcon src={CompanyAskGlyph} />}
            onClick={openEmpty}
          >
            {t('mcp_setup_builtin_title')}
          </Button>
        </Box>
        <Body color="subtle">{t('mcp_setup_builtin_body')}</Body>
      </Stack>

      <Stack gap="16">
        {HIGHLIGHT_SPECS.map((highlight) => (
          <Split key={highlight.id} gap="12" alignY="top">
            <Split.Item>
              <AvatarIcon src={highlight.icon} />
            </Split.Item>
            <Split.Item width="fill">
              <Stack gap="2">
                <Body isBold>{t(highlight.titleKey)}</Body>
                <Body size="small" color="subtle">
                  {t(highlight.descriptionKey)}
                </Body>
              </Stack>
            </Split.Item>
          </Split>
        ))}
      </Stack>

      <Stack gap="8">
        <Body size="small" color="subtle" isBold>
          {t('mcp_setup_builtin_try_it_live')}
        </Body>
        <Box display="flex" flexWrap="wrap" style={{gap: 8}}>
          {AI_SEARCH_SUGGESTIONS.map((prompt) => (
            <Chip
              key={prompt}
              variant="standard"
              size="small"
              onClick={() => runPrompt(prompt)}
              aria-label={`Ask: ${prompt}`}
            >
              <Chip.Content>{prompt}</Chip.Content>
            </Chip>
          ))}
        </Box>
      </Stack>
    </Stack>
  );
};
